import {
  DefaultError,
  isServer,
  QueryClient,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { z } from 'zod'

import { getPharmeSessionId, SESSION_HEADER } from '@/lib/sessionTracking'

/**
 * Cree un nouveau QueryClient. A appeler par requete cote serveur (SSR) pour
 * eviter toute fuite de cache inter-requetes/utilisateurs.
 */
export const makeQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
      },
    },
  })

let browserQueryClient: QueryClient | undefined

/**
 * Retourne le QueryClient adapte au contexte :
 * - SSR : toujours un client neuf par requete (pas de fuite inter-requetes/users)
 * - Navigateur : un client stable (singleton)
 */
export const getQueryClient = (): QueryClient => {
  if (isServer) {
    return makeQueryClient()
  }
  if (!browserQueryClient) browserQueryClient = makeQueryClient()
  return browserQueryClient
}

/**
 * Export retro-compatible pour les usages imperatifs cote client
 * (ex: queryClient.invalidateQueries dans un onSuccess de mutation).
 * Tous les consommateurs l'utilisent dans des callbacks client-only, donc
 * cet export resout toujours le client navigateur stable a l'usage.
 * Cote serveur, getQueryClient() renvoie un client jetable jamais utilise
 * pour le rendu (le rendu SSR passe par le client dehydrate de _app.tsx).
 */
export const queryClient = new Proxy({} as QueryClient, {
  get(_target, prop) {
    const client = getQueryClient()
    const value = (client as unknown as Record<string | symbol, unknown>)[prop]
    return typeof value === 'function' ? (value as (...args: unknown[]) => unknown).bind(client) : value
  },
})

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

interface UseRestQueryProps<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TVariables = unknown,
> extends Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'queryKey'> {
  endpoint: string
  method?: HttpMethod
  queryKey?: UseQueryOptions<TQueryFnData, TError, TData>['queryKey']
  variables?: TVariables
  queriesParams?: Record<string, string | number | boolean | (string | number)[]>
  customHeaders?: Record<string, string>
}

interface UseRestMutationProps<
  TData = unknown,
  TError = DefaultError,
  TVariables = unknown,
  TOnMutateResult = unknown,
> extends Omit<
    UseMutationOptions<TData, TError, TVariables, TOnMutateResult>,
    'mutationKey' | 'mutationFn'
  > {
  endpoint: string
  mutationKey?: UseMutationOptions<TData, TError, TVariables, TOnMutateResult>['mutationKey']
  schema?: z.ZodType<TVariables>
  method?: HttpMethod
}

const BASE_URL = '/api/csCartProxy'

export class RestApiError extends Error {
  status: number
  data: unknown

  constructor(message: string, status: number, data: unknown) {
    super(message)
    this.name = 'RestApiError'
    this.status = status
    this.data = data
  }
}

/**
 * Résout les variables de chemin (:key) dans l'endpoint
 */
const resolvePathVariables = (endpoint: string, variables?: Record<string, unknown>): string => {
  let resolved = endpoint
  if (variables && typeof variables === 'object') {
    Object.entries(variables).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      resolved = resolved.replace(`:${key}`, String(value))
    })
  }
  return resolved
}

/**
 * Ajoute les query params à l'endpoint (gère les tableaux)
 */
const appendQueryParams = (
  endpoint: string,
  queriesParams?: Record<string, string | number | boolean | (string | number)[] | unknown>
): string => {
  let resolved = endpoint
  if (queriesParams && typeof queriesParams === 'object') {
    const separator = () => (resolved.includes('?') ? '&' : '?')
    Object.entries(queriesParams).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      if (Array.isArray(value)) {
        value.forEach(v => {
          resolved = `${resolved}${separator()}${key}[]=${encodeURIComponent(String(v))}`
        })
      } else if (typeof value === 'object') {
        // Map cle -> valeur (ex: shipping_ids indexes par group_key) : PHP reconstruit un
        // tableau associatif a partir de `cle[sous_cle]=valeur`, les cles sont donc preservees.
        Object.entries(value as Record<string, unknown>).forEach(([subKey, subValue]) => {
          if (subValue === undefined || subValue === null) return
          resolved = `${resolved}${separator()}${key}[${encodeURIComponent(subKey)}]=${encodeURIComponent(
            String(subValue)
          )}`
        })
      } else {
        resolved = `${resolved}${separator()}${key}=${encodeURIComponent(String(value))}`
      }
    })
  }
  return resolved
}

/**
 * Fonction utilitaire pour effectuer des appels REST de manière impérative (sans hook React).
 * Passe par le proxy CS-Cart (/api/csCartProxy).
 */
export const fetchRest = async <T = unknown>({
  endpoint,
  method = 'GET',
  variables,
  queriesParams,
  body,
  customHeaders,
  locale,
  baseUrl,
}: {
  endpoint: string
  method?: HttpMethod
  variables?: Record<string, unknown>
  queriesParams?: Record<string, string | number | boolean | (string | number)[] | unknown>
  body?: unknown
  customHeaders?: Record<string, string>
  locale?: string
  baseUrl?: string
}): Promise<T> => {
  let resolvedEndpoint = resolvePathVariables(endpoint, variables)
  resolvedEndpoint = appendQueryParams(resolvedEndpoint, queriesParams)

  const headers: Record<string, string> = {
    'Accept-Language': locale ?? 'fr',
    ...customHeaders,
  }

  // Session analytics : UUID de session (TTL glissant 30 min) sur tous les
  // appels navigateur — forwarde par /api/csCartProxy vers CS-Cart.
  const pharmeSessionId = getPharmeSessionId()
  if (pharmeSessionId) {
    headers[SESSION_HEADER] = pharmeSessionId
  }

  if (method !== 'GET' && body) {
    headers['Content-Type'] = 'application/json'
  }

  // En SSR (Node), fetch a besoin d'une URL absolue : baseUrl porte le host courant.
  // Cote navigateur, baseUrl vaut '' et l'URL relative reste utilisee.
  const root = baseUrl ?? ''
  const response = await fetch(
    `${root}${BASE_URL}?url=${encodeURIComponent(resolvedEndpoint)}`,
    {
      method,
      headers,
      body: method !== 'GET' && body ? JSON.stringify(body) : undefined,
    }
  )

  let data: unknown = null
  if (response.status !== 204 && response.headers.get('content-length') !== '0') {
    data = await response.json()
  }

  if (!response.ok) {
    throw new RestApiError(
      (data as any)?.message || (data as any)?.error || `HTTP ${response.status}`,
      response.status,
      data
    )
  }

  // Use == null (matches null + undefined) au lieu de !data pour preserver
  // valeurs falsy valides (0, '', false). Sinon counter=0 retourne {success:true}
  // -> consumer perd valeur reelle.
  if (response.status === 204 || data == null) {
    return { success: true } as T
  }

  return data as T
}

export const useRestQuery = <
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TVariables = unknown,
>({
  endpoint,
  method,
  variables,
  queriesParams,
  customHeaders,
  queryKey,
  ...props
}: UseRestQueryProps<TQueryFnData, TError, TData, TVariables>) => {
  const router = useRouter()
  const locale = (router.query.locale as string) || 'fr'

  // La locale fait varier la reponse (Accept-Language) : on la prefixe a la cle
  // pour isoler le cache par langue, que la cle soit explicite ou par defaut.
  const finalKey = [locale, ...(queryKey ?? [endpoint, method, variables, queriesParams])]

  return useQuery<TQueryFnData, TError, TData>({
    queryFn: () => {
      return fetchRest<TQueryFnData>({
        endpoint,
        method,
        variables: variables as Record<string, unknown>,
        queriesParams,
        customHeaders,
        locale,
      })
    },
    queryKey: finalKey,
    ...props,
  })
}

export const useRestMutation = <
  TData = unknown,
  TError = DefaultError,
  TVariables = unknown,
  TOnMutateResult = unknown,
>({
  endpoint,
  schema,
  method = 'POST',
  ...props
}: UseRestMutationProps<TData, TError, TVariables, TOnMutateResult>) => {
  const router = useRouter()
  const locale = (router.query.locale as string) || 'fr'

  return useMutation<TData, TError, TVariables, TOnMutateResult>({
    mutationFn: async (data: TVariables) => {
      const _data = schema ? schema.parse(data) : data

      return fetchRest<TData>({
        endpoint,
        method,
        variables: data as Record<string, unknown>,
        body: _data ?? {},
        locale,
      })
    },
    mutationKey: [endpoint],
    ...props,
  })
}
